/* eslint-disable max-lines */
import {
  Input, ViewChild, ElementRef, ViewChildren, QueryList, ChangeDetectorRef, Component
} from '@angular/core';
import { UntypedFormGroup, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { ViewportScroller } from '@angular/common';
import { Observable, Subscription, combineLatest, zip } from 'rxjs';
import { distinctUntilChanged, map, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import isEqual from 'lodash-es/isEqual';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import isEmpty from 'lodash-es/isEmpty';

import { isNullOrUndefined } from '@app/util/util';
import { AssetTerm } from '@asset-terms';
import { environment as ENV } from '@environment';
import { User } from '@app/modules/core/interfaces/user';
import { BookingFacade } from '@booking/booking.facade';
import { BOOKING_FORM_INPUTS } from '@booking/constants/forms.constants';
import { BillingCity, BillingResponse } from '@billing/entities/billing.payload';
import { BillingFacade } from '@billing/billing.facade';
import { PaxFacade } from '@pax/pax.facade';
import { TravelerItemUI, TravelerIdentifier, TravelerRoomUI } from '@pax/entities/pax-ui';
import { TravelerPaxResponse, TravelerPaxIdentificator, TravelerPax, TravelerPaxResponseVm } from '@pax/entities/traveler-pax';
import { guestIdentificatorUI } from '@pax/mappers/guest-list-ui.mapper';
import { passengerIdentificatorUI } from '@pax/mappers/passenger-list-ui.mapper';
import { PaxSectionComponent } from '@pax/components/pax-section/pax-section.component';
import { PaxFormService } from '@pax/services/pax-form.service';
import { AMPLITUDE_EVENTS } from '@app/modules/core/constants/amplitude.constants';
import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { validAllFormSections, isValidPaxSectionByTypes } from '@booking/mappers/validate-form.mapper';
import { isActiveMobileInterface } from '@app/modules/core/mappers/mobile-interface.mapper';
import { BookingAmplitudeService } from '@booking/services/booking-amplitude.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { AmplitudeEventParams } from '@amplitude/interfaces/amplitude-events';
import { BusinessRulesByBookingType } from '@app/entities/booking-documents';
import { TIME_SHOW_CHECKOUT, TIMEOUT_ZERO } from '@booking/constants/timer.constants';
import { AssetBookingConfig } from '@asset-booking-config';
import { AppFacade } from '@app/app.facade';
import {
  AssistanceSearchResponse, AssistanceReservationInfo, AssistanceSearchPayload
} from '@shared/travel-assistance/entities/booking-assistance';
import {
  assistanceInfoByAirValidation,
  assistanceInfoByAirBySectorValidation,
  assistanceInfoByHotelValidation,
  assistanceInfoByVehicleValidation,
  assistanceReservationPayload
} from '@booking/mappers/assistance-search-payload.mapper';
import {
  BannerResultsType, BUSES_BANNER_AVAILABLE, BUSES_BANNER_ENABLED, BUSES_BANNER_PERCENTAGES,
  getBannerOfferPercentage, hasRappiCardCashbackMapper, PercentagesBanner
} from '@ux/mappers/banner-results.mapper';
import { AssistanceFacade } from '@shared/travel-assistance/assistance.facade';
import { isActivePaidChubbAssistanceByBooking } from '@shared/travel-assistance/mappers/assistance-is-active.mapper';
import {
  AirBookingPayload, AutoBookingPayload, HotelBookingPayload, PackageBookingPayload, SubmitBookingArrangement
} from '@booking/entities/booking-arrangement';
import { FullValidTravelersPipe } from '@pax/pipes/full-valid-travelers.pipe';
import { UserGender } from '@booking/entities/user-gender';
import { AvoService } from '@avo/services/avo.service';
import { TravelerListVmPipe } from '@pax/pipes/traveler-list-vm.pipe';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { isActiveFreeChubbBanner } from '@ux/mappers/assistance-banner.mapper';
import { AirValidationBySectorResponse, AirValidationResponse } from '@app/entities/air-validation.response';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';
import { AllProductsValidation } from '@app/entities/validation-types';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { bookingWaitingPageRoute, informativeBookingRoute } from '@booking/constants/booking-routes.constants';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { TriggerEvent } from '@avo/interface/trigger-entity';
import { ByHoursData } from '@booking/entities/hotel-validation.payload';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { Countries } from '@ux/entities/country.enum';
import { ActivitiesBookPayloadVM } from '@activities/entities/activities-booking';
import { 
  TypographyColor, 
  TypographyFontFamily, 
  TypographyFontSize 
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-booking-base',
  template: '',
  standalone: false
})
export class BookingBaseContainer {

  protected busBanner: BannerResultsType = BUSES_BANNER_AVAILABLE;
  protected busBannerEnabled: boolean = BUSES_BANNER_ENABLED;
  protected busBannerPercentages: PercentagesBanner = BUSES_BANNER_PERCENTAGES;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  public isActivePaidAssistance = false;
  public paxSectionReady = false;
  public travelerBookingProvider: TravelBookingType;

  @Input() public user: User;
  @Input() public isLoggedIn: boolean;
  @Input() public isMobile: boolean;
  @Input() public stickyDesktopPaymentDetail: boolean;

  @ViewChild('sectionContactDetails') public sectionContactDetails: ElementRef;
  @ViewChild('sectionTermsRef') public sectionTerms: ElementRef;
  @ViewChild('sectionBillingRef') public sectionBilling: ElementRef;
  @ViewChild('sectionExtraQuestionsRef') public sectionExtraQuestions: ElementRef;

  @ViewChildren(PaxSectionComponent, { read: ElementRef }) travelerInfoList: QueryList<ElementRef>;

  public bookingDetail: string = 'booking-detail';
  public isSubmitted: boolean = false;
  public isSubmittedSuccess: boolean = false;
  public isLoadingBookingDelay: boolean = false;
  public subscriptions: Subscription[] = [];
  public showBillingModule: boolean = false;
  public hasTravelerInfoList: boolean = false;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  protected isLoadingBookingDelay$: Subscription;

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected cdRef: ChangeDetectorRef,
    protected appFacade: AppFacade,
    protected viewportScroller: ViewportScroller,
    protected facade: BookingFacade,
    protected billingFacade: BillingFacade,
    protected paxFacade: PaxFacade,
    protected paxFormService: PaxFormService,
    protected scrollFormService: ScrollFormService,
    protected propertiesAmplitudeService: PropertiesAmplitudeService,
    protected bookingAmplitudeService: BookingAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected assistanceFacade: AssistanceFacade,
    protected fullValidTravelersPipe: FullValidTravelersPipe,
    protected avoService: AvoService,
    protected travelerListVmPipe: TravelerListVmPipe,
  ) { }

  get isRappiProBrandig$(): Observable<boolean> {
    return this.isPrimeUser$.pipe(map(isPrime => isPrime && ENV.countryInfo.iso_code === Countries.Colombia));
  }

  get countries$(): Observable<string[]> {
    return this.facade.countries$;
  }

  get cities$(): Observable<BillingCity[]> {
    return this.billingFacade.cities$;
  }

  get paxSectionReady$(): Observable<boolean> {
    return this.facade.paxSectionReady$;
  }

  get bookingReady$(): Observable<boolean> {
    return this.facade.bookingReady$;
  }

  get passengerListUI$(): Observable<TravelerItemUI[]> {
    return this.paxFacade.passengerListUI$;
  }

  get passengerListUILength$(): Observable<number> {
    return this.paxFacade.passengerListUI$.pipe(map(data => data.length));
  }

  get guestListUI$(): Observable<TravelerRoomUI[]> {
    return this.paxFacade.guestListUI$;
  }

  get travelers$(): Observable<TravelerPaxResponse[]> {
    return this.paxFacade.travelers$;
  }

  get isLoadingPaxForm$(): Observable<boolean> {
    return this.paxFacade.isLoadingTravelersForm$;
  }

  get defaultBilling$(): Observable<BillingResponse> {
    return this.facade.defaultBillingInfo$;
  }

  get conversionRate$(): Observable<number> {
    return this.facade.exchangeRate$;
  }

  get hasErrorInBooking$(): Observable<boolean> {
    return this.facade.hasErrorInBooking$;
  }

  get isAnInternationalTrip$(): Observable<boolean> {
    return this.appFacade.isAnInternationalSearch$;
  }

  get applyConversion$(): Observable<boolean> {
    return this.conversionRate$.pipe(map(rate => !!ENV.countryModules?.booking?.exchange_rate?.enabled && !!rate));
  }

  get businessRules$(): Observable<BusinessRulesByBookingType> {
    return combineLatest([this.appFacade.documentAssets$, this.paxFacade.bookingType$]).pipe(
      map(([_ENV, bookingType]) => _ENV?.BOOKING_MODULES_BUSINESS_RULES && _ENV.BOOKING_MODULES_BUSINESS_RULES[bookingType] ?
        _ENV.BOOKING_MODULES_BUSINESS_RULES[bookingType] : {} as BusinessRulesByBookingType)
    );
  }

  get showAssistanceInfo$(): Observable<boolean> {
    return this.assistanceFacade.showAssistanceInfo$;
  }

  get showAssistanceDetails$(): Observable<boolean> {
    return this.assistanceFacade.showAssistanceDetails$;
  }

  get assistanceSearchInfo$(): Observable<AssistanceSearchResponse> {
    return this.assistanceFacade.assistanceSearchInfo$;
  }

  get isPrimeUser$(): Observable<boolean> {
    return this.appFacade.isPrime$;
  }

  get hasRappiCard$(): Observable<boolean> {
    return this.appFacade.hasRappiCard$;
  }

  get isActivateFreeChubbAssistance$(): Observable<boolean> {
    return combineLatest([
      this.isAnInternationalTrip$,
      this.isPrimeUser$,
      this.paxFacade.bookingType$
    ]).pipe(map(([isAnInternationalTrip, isPrime, bookingType]) => !!isActiveFreeChubbBanner(
      bookingType, isAnInternationalTrip, isPrime
    )));
  }

  get isActivePaidAssistance$(): Observable<boolean> {
    return combineLatest([
      this.paxFacade.bookingType$,
      this.assistanceFacade.isErrorAssistanceSearch$,
      this.isActivateFreeChubbAssistance$,
    ]).pipe(
      map(([bookingType, isErrorAssistanceSearch, isActivateFree]) =>
        !isErrorAssistanceSearch && !isActivateFree && isActivePaidChubbAssistanceByBooking(bookingType))
    );
  }

  get celebratePaxView$(): Observable<UserGender> {
    return this.paxFacade.celebratePaxView$;
  }

  get isMobileDevice(): boolean {
    return isActiveMobileInterface();
  }

  get activeBillingModule(): boolean {
    return !!AssetBookingConfig.BILLING_MODULE.ENABLED;
  }

  get billingModuleIsOptional(): boolean {
    return !!AssetBookingConfig.BILLING_MODULE.IS_OPTIONAL;
  }

  get showBillingModuleData(): boolean {
    return this.billingModuleIsOptional ? this.showBillingModule : true;
  }

  get activeCallSupportModule(): boolean {
    return !!ENV.countryModules.booking && ENV.countryModules.booking.call_support;
  }

  get individualTerms(): boolean {
    return !!AssetTerm.SUMMARY_TERMS_AND_POLICIES.INDIVIDUAL_TERMS;
  }

  get capacityMicroStay(): number {
    return (JSON.parse(this.route.snapshot.paramMap.get('byHoursData')) as ByHoursData)?.capacity;
  }
  
  private get isLoadingBooking$(): Observable<boolean> {
    return this.facade.isLoadingBooking$;
  }

  private get isActiveBillingByControl(): boolean {
    return this.activeBillingModule && this.showBillingModuleData;
  }

  protected configDefaultByInfoType(
    type: TravelBookingType,
    startDate: string = null,
  ): void {
    this.travelers$.pipe(
      take(1),
      map(travelers => {
        switch (type) {
          case TravelBookingType.FLIGHTS:
          case TravelBookingType.BUSES:
          case TravelBookingType.AUTOS:
          case TravelBookingType.FLIGHTS_BY_SECTOR:
          case TravelBookingType.ACTIVITIES:
            return { travelers, uui: this.passengerUII(0) };
          case TravelBookingType.HOTELS:
          case TravelBookingType.PACKAGE_AIR:
            return { travelers, uui: this.guestUII(0, 0) };
          default:
            return { travelers, uui: null };
        }
      }),
      switchMap(({ travelers, uui }) => this.travelerListVmPipe
        .transform(travelers, TravelerTypes.ADULTS, uui, startDate)
        .pipe(
          tap(vm => this.paxFacade.configDefaultByInfoType(vm, this.user, type))
        ))
    ).subscribe().unsubscribe();
  }

  public updateTravelerPax(paxTraveler: TravelerPaxResponseVm, form: UntypedFormGroup, identifier: TravelerIdentifier): void {
    const pax = {
      ...paxTraveler,
      email: form.get(BOOKING_FORM_INPUTS.EMAIL).value,
      phone: form.get(BOOKING_FORM_INPUTS.PHONE).value
    };
    const isStaticTravelerId = this.paxFormService.isStaticTravelerId(pax._id);
    if (!isStaticTravelerId) {
      this.paxFacade.updatePax(pax, identifier);
    } else {
      this.paxFacade.onlyUpdateToList(pax, identifier);
    }
  }

  public deleteTravelerPax(identificator: TravelerPaxIdentificator, id: number, guestId?: number): void {
    const isStaticTravelerId = this.paxFormService.isStaticTravelerId(identificator._id);
    if (!isStaticTravelerId) this.paxFacade.deletePax(identificator, id, guestId);
    else this.paxFacade.onlyDeleteToList(identificator, id, guestId);
  }

  public selectTravelerPax(travelerPax: TravelerPaxResponse, id: number, guestId: number = null): void {
    if (!isNullOrUndefined(guestId)) {
      this.travelers$.pipe(
        take(1),
        map(travelers => ({ list: travelers.filter(traveler => traveler.ui === this.guestUII(id, guestId)) })),
        tap(({ list }) => this.paxFacade.setTavelerPax(travelerPax, id, list, null, guestId))
      ).subscribe().unsubscribe();
    } else {
      zip(
        this.travelers$,
        this.facade.airValidation$
      ).pipe(
        map(([travelers, airValidation]) => ({
          defaultAirline: this.paxFormService.defaultAirline(airValidation),
          list: travelers.filter(traveler => traveler.ui === this.passengerUII(id))
        })),
        tap(({ defaultAirline, list }) => this.paxFacade.setTavelerPax(travelerPax, id, list, defaultAirline))
      ).subscribe().unsubscribe();
    }
  }

  public createTravelerPax(
    travelerPax: TravelerPax, form: UntypedFormGroup, identifier: TravelerIdentifier, startDate: string = null
  ): void {
    zip(
      this.paxFacade.isCreateTraveler$.pipe(take(1)),
      this.travelers$.pipe(
        take(1),
        switchMap(travelers => this.fullValidTravelersPipe.transform(travelers, startDate).pipe(
          map(fullVmList => fullVmList?.length === 0),
        )),
      )
    ).pipe(
      map(([isCreateTraveler, celebrateAfterCreatedPax]) => {
        return {
          isCreateTraveler, celebrateAfterCreatedPax,
          pax: {
            ...travelerPax, 
            email: form.get(BOOKING_FORM_INPUTS.EMAIL).value,
            phone: form.get(BOOKING_FORM_INPUTS.PHONE).value
          }
        };
      }),
      tap(({ isCreateTraveler, pax, celebrateAfterCreatedPax }) => {
        if (isCreateTraveler) {
          this.paxFacade.createPax(pax, identifier, celebrateAfterCreatedPax);
        } else {
          this.paxFacade.onlyAddToList(pax, identifier);
        }
      })
    ).subscribe().unsubscribe();
  }

  public setCreatePAXUI(payload: boolean): void {
    this.paxFacade.setCreatePAXUI(payload);
  }

  public guestUII(id: number, guestId: number): string {
    return guestIdentificatorUI(id, guestId);
  }

  public passengerUII(id: number): string {
    return passengerIdentificatorUI(id);
  }

  public trackBookingAmplitudeEvent(
    event: AmplitudeEventParams, type: TravelBookingType): void {
    switch (event.type) {
      case AMPLITUDE_EVENTS.SELECT_VIEW_PAX_LIST:
      case AMPLITUDE_EVENTS.SELECT_ADD_PAX:
      case AMPLITUDE_EVENTS.SELECT_EDIT_PAX:
        this.bookingAmplitudeService.triggerBookingWithProductProps$(event.type, event.props, type).pipe(
          tap(trigger => triggerAmplitudeEventApp(trigger))
        ).subscribe().unsubscribe();
        break;
      case AVO_EVENTS.SELECT_TRAVEL_ASSISTANCE:
        this.avoService.getEventTriggerProps$(event.type, event.props).pipe(
          tap(trigger => {
            this.AvoDispatch.selectTravelAssistance({
              ...this.avoService.avoBaseProperties(trigger),
              ...this.avoService.avoSearchProps(event.props),
              planCode: trigger?.EVENT_PROPS?.PLAN_CODE as string,
              planName: trigger?.EVENT_PROPS?.PLAN_NAME as string,
              unitPrice: trigger?.EVENT_PROPS?.UNIT_PRICE as number,
              vendorName: trigger?.EVENT_PROPS?.VENDOR_NAME as string
            });
          })
        ).subscribe().unsubscribe();
      case AVO_EVENTS.VIEW_PAX_FORM:
        this.avoService.getEventTriggerProps$(event.type, event.props).pipe(
          tap(trigger => {
            this.AvoDispatch.viewPaxForm({
              ...this.avoService.avoBaseProperties(trigger)
            });
          })
        ).subscribe().unsubscribe();
        break;
      default:
        break;
    }
  }

  public closeAssistanceInfo(): void {
    this.assistanceFacade.toggleShowAssistanceInfoAction(false);
  }

  public setShowAssistanceDetails(payload: boolean): void {
    if (!this.isLoadingBookingDelay) {
      this.assistanceFacade.setShowAssistanceDetails(payload);
    }
  }

  protected submitToAssistanceServiceSubscription$(
    info: AssistanceReservationInfo, currencyCode: string, params: ParamMap
  ): Observable<AssistanceSearchPayload> {
    return zip(this.appFacade.searchData$.pipe(take(1)), this.appFacade.user$.pipe(take(1)))
      .pipe(
        map(([searchData, user]) => assistanceReservationPayload(info, currencyCode, searchData, user, params))
      );
  }

  protected fetchAssistanceModal(form: UntypedFormGroup, eventProps: TriggerEvent, isDisabled: boolean): void {
    this.isSubmitted = true;
    if (isDisabled) {
      this.validateAllFormFields(form);
      this.validateSectionFormToScroll(form);
    } else {
      this.assistanceFacade.toggleShowAssistanceInfoAction(true);
      this.AvoDispatch.viewTravelAssistanceModal({
        ...this.avoService.avoBaseProperties(eventProps),
        ...this.avoService.avoSearchProps(eventProps?.SEARCH_PROPS),
        ...this.avoService.avoProductProps(eventProps?.PRODUCT_PROPS),
        planCode: eventProps?.EVENT_PROPS?.PLAN_CODE as string,
        planName: eventProps?.EVENT_PROPS?.PLAN_NAME as string,
        unitPrice: eventProps?.EVENT_PROPS?.UNIT_PRICE as number,
        vendorName: eventProps?.EVENT_PROPS?.VENDOR_NAME as string
      });
    }
  }

  protected listenFormChanges(form: UntypedFormGroup): void {
    this.subscriptions.push(
      combineLatest([
        this.passengerListUI$,
        this.guestListUI$,
        this.paxFacade.bookingType$.pipe(skipWhile(type => !type), take(1)),
        form.get(BOOKING_FORM_INPUTS.TRAVELERS).statusChanges.pipe(
          distinctUntilChanged((current, preview) => isEqual(current, preview)))
      ]).pipe(
        map(([passengerListUI, guestListUI, bookingType]) => isValidPaxSectionByTypes(
          form, passengerListUI, guestListUI, bookingType
        )),
        tap(isValid => this.facade.paxSectionReadyControl(isValid))
      ).subscribe(),
      this.paxSectionReady$.pipe(
        distinctUntilChanged((current, preview) => isEqual(current, preview)),
        map(paxSectionReady => paxSectionReady && validAllFormSections(form)),
        tap(isValid => this.facade.bookingReadyControl(isValid, this.isActiveBillingByControl))
      ).subscribe(),
      form.valueChanges.pipe(
        distinctUntilChanged((current, preview) => isEqual(current, preview)),
        tap(() => {
          this.facade.bookingReadyControl(validAllFormSections(form), this.isActiveBillingByControl);
        })
      ).subscribe()
    );
  }

  protected validateSectionFormToScroll(formGroup: UntypedFormGroup): void {
    this.billingFacade.hasDefaultBilling$.pipe(
      take(1),
      tap(hasDefaultBilling => {
        const defaultBillingValue = this.isActiveBillingByControl ? !!hasDefaultBilling : true;
        this.scrollFormService.validateSectionFormToScroll(
          formGroup, this.sectionContactDetails, this.sectionBilling,
          this.sectionTerms, this.sectionExtraQuestions, this.travelerInfoList, defaultBillingValue
        );
      })
    ).subscribe().unsubscribe();
  }

  protected validateAllFormFields(formGroup: UntypedFormGroup): void {
    if (formGroup?.controls) {
      Object.keys(formGroup.controls)?.forEach(field => {
        const control = formGroup.get(field);
        if (control instanceof UntypedFormControl) control.markAsTouched({ onlySelf: true });
        else if (control instanceof UntypedFormGroup) this.validateAllFormFields(control);
        else if (control instanceof UntypedFormArray) this.validateAllFormFields(control.controls[0] as UntypedFormGroup);
      });
    }
  }

  protected resetbillingForm(formGroup: UntypedFormGroup): void {
    if (!this.showBillingModule && formGroup && formGroup.get(BOOKING_FORM_INPUTS.BILLING)) {
      formGroup.removeControl(BOOKING_FORM_INPUTS.BILLING);
      formGroup.addControl(BOOKING_FORM_INPUTS.BILLING, new UntypedFormGroup({}));
    }
  }

  protected isLoadingBookingDelaySubscription(): Subscription {
    return this.isLoadingBooking$.pipe(
      distinctUntilChanged(),
      switchMap(isLoadingBooking => this.hasErrorInBooking$.pipe(take(1)).pipe(
        tap(hasErrorInBooking => {
          if (this.isMobile && (this.isLoadingBookingDelay && !isLoadingBooking)) {
            setTimeout(() => this.isLoadingBookingDelay = false, !hasErrorInBooking ? TIME_SHOW_CHECKOUT : 0);
          } else if (isLoadingBooking) this.isLoadingBookingDelay = true;
        })
      ))
    ).subscribe();
  }

  protected hasRappiCardCashbackAbstract(
    isEnabled: boolean, type: BannerResultsType, percentages: PercentagesBanner, reference: number | string
  ): boolean {
    return hasRappiCardCashbackMapper(isEnabled, percentages, reference);
  }

  protected bannerPercentagesAbstract(percentages: PercentagesBanner, reference: number | string, code: string): number {
    return getBannerOfferPercentage(percentages, reference, code);
  }

  protected submitByBookServiceAction(
    submit: SubmitBookingArrangement, formGroup: UntypedFormGroup, type: TravelBookingType, searchBy?: SearchBy): void {
    this.isSubmitted = true;
    this.validateAllFormFields(formGroup);
    if (isEmpty(submit?.props)) {
      this.validateSectionFormToScroll(formGroup);
    } else {
      this.isSubmittedSuccess = true;
      switch (type) {
        case TravelBookingType.AUTOS:
          this.facade.setBookingAutoArrangement(submit.props as AutoBookingPayload);
          break;
        case TravelBookingType.BUSES:
        case TravelBookingType.FLIGHTS:
          this.facade.setBookingAirArrangement(submit.props as AirBookingPayload);
          break;
        case TravelBookingType.FLIGHTS_BY_SECTOR:
          this.facade.setBookingAirBySectorArrangement(submit.props as AirBookingPayload);
          break;
        case TravelBookingType.HOTELS:
          this.facade.setBookingHotelArrangement(submit.props as HotelBookingPayload, searchBy);
          break;
        case TravelBookingType.PACKAGE_AIR:
          this.facade.setBookingPackageArrangement(submit.props as PackageBookingPayload);
          break;
        case TravelBookingType.ACTIVITIES:
          this.facade.setBookingActivitiesArrangement(submit.props as ActivitiesBookPayloadVM);
          break;
      }
      if (!isEmpty(submit?.trigger)) {
        this.AvoDispatch.proceedToCheckout({
          ...this.avoService.avoBaseProperties(submit.trigger),
          ...this.avoService.avoProductProps(submit.trigger.PRODUCT_PROPS),
          ...this.avoService.avoSearchProps(submit.trigger.SEARCH_PROPS),
          source: this.avoService.getAvoSourceValueType(submit.trigger.EVENT_PROPS?.SOURCE),
          itineraryId: submit.trigger.EVENT_PROPS?.ITINERARY_ID as number,
        });
      }
      zip(
        this.facade.airValidation$, this.facade.airValidationBySector$,
        this.facade.hotelValidation$, this.facade.autoValidation$, this.facade.activitiesValidation$
      ).pipe(
        map(([airValidation, airValidationBySector, hotelValidation, autoValidation, activitiesValidation]) => ({
          airValidation, airValidationBySector, hotelValidation, autoValidation, activitiesValidation
        })),
        tap(({airValidation, airValidationBySector, hotelValidation, autoValidation, activitiesValidation,}) => {
          this.router.navigate([{ outlets: { [informativeBookingRoute]: [bookingWaitingPageRoute] } }], {
            state: {
              props: submit.props, 
              type,
              isMobile: this.isMobile,
              airValidation, airValidationBySector, hotelValidation, autoValidation, activitiesValidation,
              capacity: this.capacityMicroStay
            },
            relativeTo: this.route, skipLocationChange: true
          });
        })
      ).subscribe().unsubscribe();
    }
  }

  protected checkPaxSection(): void {
    setTimeout(() => {
      this.hasTravelerInfoList = !!this.travelerInfoList?.length;
      this.cdRef.detectChanges();
    }, 0);
  }

  protected listenToAssistanceSearchCall(
    validation: AllProductsValidation,
    currencyCode: string,
    paramMap: ParamMap): void {
    this.subscriptions.push(
      combineLatest([
        this.paxSectionReady$.pipe(distinctUntilChanged()),
        this.isActivePaidAssistance$.pipe(distinctUntilChanged()),
        this.paxFacade.passengerListUI$.pipe(distinctUntilChanged()),
        this.paxFacade.guestListUI$.pipe(distinctUntilChanged())
      ]).subscribe(([paxSectionReady, isActivePaidAssistance, passengerListUI, guestListUI]) => {
        this.isActivePaidAssistance = isActivePaidAssistance;
        setTimeout(() => {
          const mappedTravelers = this.travelerBookingProvider !== TravelBookingType.HOTELS &&
            this.travelerBookingProvider !== TravelBookingType.PACKAGE_AIR ? passengerListUI.map(listUI => listUI.defaultData) : 
            [].concat(...guestListUI.map(listUI => listUI.roomGuests.map(guest => guest.defaultData)));
          this.paxSectionReady = paxSectionReady;
          if (paxSectionReady && isActivePaidAssistance) {
            this.fetchAssistanceSearch(
              this.assistanceInfoCall(validation, mappedTravelers),
              currencyCode,
              paramMap
            );
          }
        }, TIMEOUT_ZERO);
      })
    );
  }

  protected fetchAssistanceSearch(assistance: AssistanceReservationInfo, currencyCode: string, params: ParamMap) {
    this.submitToAssistanceServiceSubscription$(assistance, currencyCode, params
    ).subscribe(payload => {
      this.assistanceFacade.fetchAssistanceSearch(payload, this.travelerBookingProvider);
    }).unsubscribe();
  }

  private assistanceInfoCall(
    validation: AllProductsValidation,
    mappedTravelers: TravelerPaxResponse[]): AssistanceReservationInfo {
    switch (this.travelerBookingProvider) {
      case TravelBookingType.FLIGHTS_BY_SECTOR:
        return assistanceInfoByAirBySectorValidation(validation as AirValidationBySectorResponse, mappedTravelers);
      case TravelBookingType.HOTELS:
        return assistanceInfoByHotelValidation(validation as HotelValidationResponse, mappedTravelers);
      case TravelBookingType.AUTOS:
        return assistanceInfoByVehicleValidation(validation as VehicleValidateResponse, mappedTravelers)
      default:
        return assistanceInfoByAirValidation(validation as AirValidationResponse, mappedTravelers);
    }
  }
}
